import React, { useState, useEffect } from 'react';
import { Dumbbell, Award, Users, Clock, MapPin, Phone, Mail, MessageCircle, Star, CheckCircle, Calculator, Apple, Zap, TrendingUp, Menu, X, ChevronUp } from 'lucide-react';

export default function MYFITGym() {
  const [formData, setFormData] = useState({ name: '', phone: '', goal: '' });
  const [bmiData, setBmiData] = useState({ weight: '', height: '', result: null, category: '', color: '' });
  const [mobileMenu, setMobileMenu] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('monthly');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const calculateBMI = () => {
    if (!bmiData.weight || !bmiData.height) {
      alert('Please enter both weight and height');
      return;
    }
    const heightInMeters = bmiData.height / 100;
    const bmi = (bmiData.weight / (heightInMeters * heightInMeters)).toFixed(1);
    let category = '', color = '';
    
    if (bmi < 18.5) {
      category = 'Underweight';
      color = 'text-blue-400';
    } else if (bmi >= 18.5 && bmi < 25) {
      category = 'Normal Weight';
      color = 'text-green-400';
    } else if (bmi >= 25 && bmi < 30) {
      category = 'Overweight';
      color = 'text-yellow-400';
    } else {
      category = 'Obese';
      color = 'text-red-400';
    }
    
    setBmiData({...bmiData, result: bmi, category, color});
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.phone || !formData.goal) {
      alert('Please fill in all fields');
      return;
    }
    const message = `Hello MYFIT! I'd like to book a free trial.%0AName: ${formData.name}%0APhone: ${formData.phone}%0AGoal: ${formData.goal}`;
    window.open(`https://wa.me/919673129769?text=${message}`, '_blank');
    setFormData({ name: '', phone: '', goal: '' });
    setSuccessMessage('Request sent! Check your WhatsApp.');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleWhatsApp = (planName, duration) => {
    const message = `Hi MYFIT! I'm interested in the ${planName} plan for ${duration}. Please share details.`;
    window.open(`https://wa.me/919673129769?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleDietConsultation = () => {
    const message = "Hi MYFIT! I'm interested in a personalized diet plan. Please share details.";
    window.open(`https://wa.me/919673129769?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleJoinNow = () => {
    const message = "Hi MYFIT! I want to join your gym. Please guide me through the membership process.";
    window.open(`https://wa.me/919673129769?text=${encodeURIComponent(message)}`, '_blank');
  };

  const plans = [
    {
      name: 'BASIC',
      features: ['Full Gym Access', 'Cardio Zone', 'Locker Facility', 'Shower Access', 'Free Induction'],
      pricing: {
        monthly: { original: 4999, discounted: 3499 },
        quarterly: { original: 13999, discounted: 8999 },
        halfYearly: { original: 26999, discounted: 16999 },
        yearly: { original: 49999, discounted: 29999 }
      }
    },
    {
      name: 'PRO',
      features: ['All Basic Features', 'Personal Trainer (3x/week)', 'Diet Consultation', 'Steam & Sauna', 'Group Classes', 'Priority Support'],
      popular: true,
      pricing: {
        monthly: { original: 8999, discounted: 6499 },
        quarterly: { original: 25999, discounted: 17999 },
        halfYearly: { original: 49999, discounted: 33999 },
        yearly: { original: 89999, discounted: 59999 }
      }
    },
    {
      name: 'ELITE',
      features: ['All Pro Features', 'Daily Personal Training', 'Swimming Pool', 'Sports Turf (4hrs/week)', 'Custom Meal Plans', 'Spa & Massage', '24/7 Access'],
      pricing: {
        monthly: { original: 14999, discounted: 10999 },
        quarterly: { original: 42999, discounted: 29999 },
        halfYearly: { original: 79999, discounted: 54999 },
        yearly: { original: 149999, discounted: 99999 }
      }
    }
  ];

  const trainers = [
    { name: 'Rahul Sharma', title: 'Head Strength Coach', exp: '12 Years', image: 'https://images.unsplash.com/photo-1567013127542-490d757e51fc?w=400&h=500&fit=crop&auto=format' },
    { name: 'Priya Patel', title: 'Yoga & Flexibility', exp: '8 Years', image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=500&fit=crop&auto=format' },
    { name: 'Arjun Singh', title: 'Bodybuilding Expert', exp: '15 Years', image: 'https://images.unsplash.com/photo-1605296867304-46d5465a13f1?w=400&h=500&fit=crop&auto=format' }
  ];

  const transformations = [
    {
      name: 'Vikram',
      before: 'https://images.unsplash.com/photo-1583468982228-19f19164aee2?w=400&h=400&fit=crop&auto=format',
      after: 'https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?w=400&h=400&fit=crop&auto=format',
      result: 'Lost 15kg in 3 months'
    },
    {
      name: 'Sneha',
      before: 'https://images.unsplash.com/photo-1502139214982-d0ad755818d8?w=400&h=400&fit=crop&auto=format',
      after: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=400&fit=crop&auto=format',
      result: 'Achieved dream body in 4 months'
    }
  ];

  const mealPlans = [
    { name: 'Weight Loss', calories: '1500-1800 cal/day', meals: ['High Protein Breakfast', 'Light Lunch', 'Protein Shake', 'Grilled Chicken'], icon: TrendingUp },
    { name: 'Muscle Gain', calories: '2800-3200 cal/day', meals: ['Oats with Eggs', 'Chicken Rice Bowl', 'Post-Workout Shake', 'Fish with Sweet Potato'], icon: Dumbbell },
    { name: 'Balanced Diet', calories: '2000-2200 cal/day', meals: ['Mixed Grain Breakfast', 'Balanced Lunch', 'Evening Snacks', 'Light Dinner'], icon: Apple }
  ];

  const whyChoose = [
    { icon: Award, title: 'PREMIUM EQUIPMENT', desc: 'International-grade machines from Technogym, Life Fitness & Matrix.' },
    { icon: Users, title: 'EXPERT TRAINERS', desc: 'Certified professionals with 8-15 years of experience.' },
    { icon: Star, title: 'LUXURY AMBIENCE', desc: '10,000 sq ft air-conditioned facility with premium amenities.' }
  ];

  const durationLabels = { monthly: 'Monthly', quarterly: '3 Months', halfYearly: '6 Months', yearly: '1 Year' };

  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden">
      {/* Offer Banner */}
      <div className="bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 text-black py-3 px-4 text-center font-bold text-sm md:text-base">
        <Zap className="inline mr-2" size={18} />
        NEW YEAR SPECIAL: 50% OFF Annual Memberships + Free Diet Consultation!
        <Zap className="inline ml-2" size={18} />
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 w-full bg-black bg-opacity-95 backdrop-blur-md z-50 border-b border-yellow-600 border-opacity-20 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Dumbbell className="text-yellow-500" size={28} />
            <span className="text-xl md:text-2xl font-bold tracking-wider">MYFIT</span>
          </div>
          
          <div className="hidden lg:flex gap-8 text-sm uppercase tracking-wide">
            <a href="#about" className="hover:text-yellow-500 transition-colors duration-300">About</a>
            <a href="#membership" className="hover:text-yellow-500 transition-colors duration-300">Membership</a>
            <a href="#nutrition" className="hover:text-yellow-500 transition-colors duration-300">Nutrition</a>
            <a href="#trainers" className="hover:text-yellow-500 transition-colors duration-300">Trainers</a>
            <a href="#contact" className="hover:text-yellow-500 transition-colors duration-300">Contact</a>
          </div>
          
          <div className="flex items-center gap-4">
            <button onClick={handleJoinNow} className="hidden md:block bg-yellow-500 text-black px-6 py-2 rounded-full font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-lg hover:shadow-yellow-500/50">
              Join Now
            </button>
            <button onClick={() => setMobileMenu(!mobileMenu)} className="lg:hidden text-yellow-500">
              {mobileMenu ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
        
        {mobileMenu && (
          <div className="lg:hidden bg-zinc-900 border-t border-yellow-600 border-opacity-20">
            <div className="flex flex-col py-4 px-6 space-y-4 text-sm uppercase tracking-wide">
              <a href="#about" onClick={() => setMobileMenu(false)} className="hover:text-yellow-500 transition">About</a>
              <a href="#membership" onClick={() => setMobileMenu(false)} className="hover:text-yellow-500 transition">Membership</a>
              <a href="#nutrition" onClick={() => setMobileMenu(false)} className="hover:text-yellow-500 transition">Nutrition</a>
              <a href="#trainers" onClick={() => setMobileMenu(false)} className="hover:text-yellow-500 transition">Trainers</a>
              <a href="#contact" onClick={() => setMobileMenu(false)} className="hover:text-yellow-500 transition">Contact</a>
              <button onClick={handleJoinNow} className="bg-yellow-500 text-black px-6 py-2 rounded-full font-semibold text-center hover:bg-yellow-400 transition">Join Now</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black z-10"></div>
        <img src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&h=1080&fit=crop" alt="Fitness Hero" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative z-20 text-center px-4 md:px-6 max-w-6xl py-20">
          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-6 tracking-tight leading-tight">WHERE STRENGTH<br /><span className="text-yellow-500">MEETS LUXURY</span></h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-12 text-gray-300 tracking-wide max-w-3xl mx-auto">Elite Training. Premium Equipment. Real Results.</p>
          <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-center">
            <button onClick={() => document.getElementById('trial').scrollIntoView({ behavior: 'smooth' })} className="w-full sm:w-auto bg-yellow-500 text-black px-8 md:px-10 py-3 md:py-4 rounded-full text-base md:text-lg font-bold hover:bg-yellow-400 transition-all duration-300 shadow-lg shadow-yellow-500/50 hover:scale-105">Book Free Trial</button>
            <a href="#membership" className="w-full sm:w-auto border-2 border-yellow-500 text-yellow-500 px-8 md:px-10 py-3 md:py-4 rounded-full text-base md:text-lg font-bold hover:bg-yellow-500 hover:text-black transition-all duration-300">View Plans</a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 md:py-20 px-4 md:px-6 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">ABOUT <span className="text-yellow-500">MYFIT</span></h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-12"></div>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <img src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&h=600&fit=crop" alt="Luxury Gym Interior" className="rounded-lg shadow-2xl w-full" />
            </div>
            <div className="order-1 md:order-2">
              <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-6">MYFIT is an exclusive fitness destination where elite athletes and ambitious individuals transform their bodies and minds. We combine world-class equipment, expert trainers, and a luxurious environment to deliver unparalleled results.</p>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-8">Premium quality breeds premium results. Every detail, from our state-of-the-art machines to personalized training programs, is designed to exceed expectations.</p>
              <div className="grid grid-cols-3 gap-4 md:gap-6 text-center">
                <div className="bg-black p-4 rounded-lg border border-yellow-600 border-opacity-20"><div className="text-3xl md:text-4xl font-bold text-yellow-500">500+</div><div className="text-xs md:text-sm text-gray-400 uppercase mt-1">Members</div></div>
                <div className="bg-black p-4 rounded-lg border border-yellow-600 border-opacity-20"><div className="text-3xl md:text-4xl font-bold text-yellow-500">15+</div><div className="text-xs md:text-sm text-gray-400 uppercase mt-1">Trainers</div></div>
                <div className="bg-black p-4 rounded-lg border border-yellow-600 border-opacity-20"><div className="text-3xl md:text-4xl font-bold text-yellow-500">10K+</div><div className="text-xs md:text-sm text-gray-400 uppercase mt-1">Sq Ft</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-zinc-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">WHY CHOOSE <span className="text-yellow-500">MYFIT</span></h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-12 md:mb-16"></div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {whyChoose.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <div key={idx} className="bg-black p-6 md:p-8 rounded-lg border border-yellow-600 border-opacity-20 hover:border-yellow-500 transition-all duration-300 hover:scale-105">
                  <IconComponent className="text-yellow-500 mb-4" size={48} />
                  <h3 className="text-xl md:text-2xl font-bold mb-4">{item.title}</h3>
                  <p className="text-gray-400 text-sm md:text-base leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">SUCCESS <span className="text-yellow-500">STORIES</span></h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6"></div>
          <p className="text-center text-gray-400 mb-12 md:mb-16 text-sm md:text-base">Real transformations from our dedicated members</p>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {transformations.map((person, idx) => (
              <div key={idx} className="bg-zinc-900 p-6 md:p-8 rounded-lg border border-yellow-600 border-opacity-20 hover:border-yellow-500 transition-all duration-300">
                <h3 className="text-2xl font-bold mb-6 text-center text-yellow-500">{person.name}'s Journey</h3>
                <div className="flex items-center justify-center gap-4 md:gap-6 mb-6">
                  <div className="text-center flex-1">
                    <img src={person.before} alt={`${person.name} before`} className="w-full aspect-square object-cover rounded-lg mx-auto mb-3 border-4 border-red-500 shadow-lg" />
                    <span className="text-sm text-gray-400 uppercase font-semibold">Before</span>
                  </div>
                  <div className="text-yellow-500 text-2xl md:text-3xl font-bold">→</div>
                  <div className="text-center flex-1">
                    <img src={person.after} alt={`${person.name} after`} className="w-full aspect-square object-cover rounded-lg mx-auto mb-3 border-4 border-green-500 shadow-lg" />
                    <span className="text-sm text-gray-400 uppercase font-semibold">After</span>
                  </div>
                </div>
                <p className="text-center text-lg font-semibold text-yellow-500 mb-4">{person.result}</p>
                <div className="flex items-center justify-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-yellow-500 fill-yellow-500" size={20} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BMI Calculator */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">BMI <span className="text-yellow-500">CALCULATOR</span></h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6"></div>
          <p className="text-center text-gray-400 mb-10 md:mb-12 text-sm md:text-base">Calculate your Body Mass Index and discover your fitness baseline</p>
          <div className="bg-zinc-900 p-6 md:p-8 rounded-lg border border-yellow-600 border-opacity-20">
            <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold mb-2 uppercase tracking-wide">Weight (kg)</label>
                <input type="number" value={bmiData.weight} onChange={(e) => setBmiData({...bmiData, weight: e.target.value})} className="w-full bg-black border border-yellow-600 border-opacity-30 rounded px-4 py-3 focus:outline-none focus:border-yellow-500 transition" placeholder="e.g. 70" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 uppercase tracking-wide">Height (cm)</label>
                <input type="number" value={bmiData.height} onChange={(e) => setBmiData({...bmiData, height: e.target.value})} className="w-full bg-black border border-yellow-600 border-opacity-30 rounded px-4 py-3 focus:outline-none focus:border-yellow-500 transition" placeholder="e.g. 175" />
              </div>
            </div>
            <button onClick={calculateBMI} className="w-full bg-yellow-500 text-black py-3 md:py-4 rounded-full text-base md:text-lg font-bold hover:bg-yellow-400 transition-all duration-300 mb-6 flex items-center justify-center gap-2">
              <Calculator size={20} />
              Calculate BMI
            </button>
            
            {bmiData.result && (
              <div className="bg-black p-6 md:p-8 rounded-lg border border-yellow-500 text-center">
                <div className="text-5xl md:text-6xl font-bold text-yellow-500 mb-2">{bmiData.result}</div>
                <div className={`text-2xl md:text-3xl font-semibold mb-6 ${bmiData.color}`}>{bmiData.category}</div>
                <div className="text-gray-400 text-xs md:text-sm mb-6">
                  <p className="mb-3 font-semibold">BMI Categories:</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="bg-zinc-900 p-2 rounded">Underweight: &lt;18.5</div>
                    <div className="bg-zinc-900 p-2 rounded">Normal: 18.5-24.9</div>
                    <div className="bg-zinc-900 p-2 rounded">Overweight: 25-29.9</div>
                    <div className="bg-zinc-900 p-2 rounded">Obese: ≥30</div>
                  </div>
                </div>
                <button onClick={() => document.getElementById('trial').scrollIntoView({ behavior: 'smooth' })} className="inline-block bg-yellow-500 text-black px-6 md:px-8 py-3 rounded-full font-semibold hover:bg-yellow-400 transition-all duration-300 hover:scale-105">Start Your Transformation</button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Membership Plans */}
      <section id="membership" className="py-16 md:py-20 px-4 md:px-6 bg-zinc-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">MEMBERSHIP <span className="text-yellow-500">PLANS</span></h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6"></div>
          <p className="text-center text-gray-400 mb-8 text-sm md:text-base">Choose the perfect plan to match your fitness journey</p>

          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-12">
            {Object.keys(durationLabels).map((duration) => (
              <button key={duration} onClick={() => setSelectedDuration(duration)} className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${selectedDuration === duration ? 'bg-yellow-500 text-black scale-105 shadow-lg shadow-yellow-500/50' : 'bg-black text-gray-400 border border-yellow-600 border-opacity-20 hover:border-yellow-500'}`}>
                {durationLabels[duration]}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {plans.map((plan, idx) => (
              <div key={idx} className={`relative bg-black p-6 md:p-8 rounded-lg border-2 transition-all duration-300 ${plan.popular ? 'border-yellow-500 md:scale-105 shadow-xl shadow-yellow-500/20' : 'border-yellow-600 border-opacity-20 hover:border-yellow-500'}`}>
                {plan.popular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-black px-4 py-1 rounded-full text-xs md:text-sm font-bold">MOST POPULAR</div>}
                <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">{plan.name}</h3>
                
                <div className="text-center mb-6 md:mb-8">
                  <div className="relative inline-block">
                    <div className="text-2xl md:text-3xl text-gray-500 line-through mb-1">₹{plan.pricing[selectedDuration].original.toLocaleString('en-IN')}</div>
                    <div className="text-4xl md:text-5xl font-bold text-yellow-500">₹{plan.pricing[selectedDuration].discounted.toLocaleString('en-IN')}</div>
                    <div className="absolute -top-3 -right-12 md:-right-16 bg-red-600 text-white text-xs md:text-sm px-2 py-1 rounded-full font-bold rotate-12">SAVE {Math.round(((plan.pricing[selectedDuration].original - plan.pricing[selectedDuration].discounted) / plan.pricing[selectedDuration].original) * 100)}%</div>
                  </div>
                  <div className="text-gray-400 text-sm mt-2">/{durationLabels[selectedDuration]}</div>
                </div>

                <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="text-yellow-500 flex-shrink-0 mt-1" size={18} />
                      <span className="text-gray-300 text-sm md:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => handleWhatsApp(plan.name, durationLabels[selectedDuration])} className={`w-full text-center py-3 rounded-full font-bold transition-all duration-300 ${plan.popular ? 'bg-yellow-500 text-black hover:bg-yellow-400 hover:scale-105' : 'border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black'}`}>
                  Select Plan
                </button>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 text-sm mb-4">💳 Flexible payment options | 🎁 Special discounts on longer durations</p>
            <p className="text-yellow-500 text-xs">* All prices are inclusive of GST | Limited time offer</p>
          </div>
        </div>
      </section>

      {/* Nutrition */}
      <section id="nutrition" className="py-16 md:py-20 px-4 md:px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">NUTRITION & <span className="text-yellow-500">DIET PLANS</span></h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6"></div>
          <p className="text-center text-gray-400 mb-12 md:mb-16 max-w-3xl mx-auto text-sm md:text-lg">Achieve your fitness goals faster with expertly crafted meal plans. Each plan is customized by certified nutritionists.</p>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-10 md:mb-12">
            {mealPlans.map((plan, idx) => {
              const IconComponent = plan.icon;
              return (
                <div key={idx} className="bg-zinc-900 p-6 md:p-8 rounded-lg border border-yellow-600 border-opacity-20 hover:border-yellow-500 transition-all duration-300 hover:scale-105">
                  <IconComponent className="text-yellow-500 mb-4" size={48} />
                  <h3 className="text-xl md:text-2xl font-bold mb-3">{plan.name}</h3>
                  <div className="text-yellow-500 font-semibold mb-6 text-sm md:text-base">{plan.calories}</div>
                  <ul className="space-y-3 mb-6">
                    {plan.meals.map((meal, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="text-yellow-500 flex-shrink-0 mt-1" size={16} />
                        <span className="text-gray-400 text-xs md:text-sm">{meal}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <div className="bg-gradient-to-r from-zinc-800 to-zinc-900 p-6 md:p-8 rounded-lg border border-yellow-500">
            <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">Get Your <span className="text-yellow-500">Personalized Meal Plan</span></h3>
                <p className="text-gray-400 mb-6 text-sm md:text-base">Our nutrition experts create custom diet plans tailored to your goals, lifestyle, and preferences. Includes meal prep guides, grocery lists, and recipes.</p>
                <ul className="space-y-3 mb-6">
                  {['Macro-balanced meals', 'Indian & continental options', 'Weekly progress tracking', 'Supplement recommendations'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className="text-yellow-500 flex-shrink-0" size={20} />
                      <span className="text-sm md:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <img src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&h=400&fit=crop" alt="Healthy Meal" className="rounded-lg mb-6 shadow-2xl w-full" />
                <button onClick={handleDietConsultation} className="bg-yellow-500 text-black px-6 md:px-8 py-3 md:py-4 rounded-full font-bold hover:bg-yellow-400 transition-all duration-300 inline-flex items-center gap-2 hover:scale-105">
                  <Apple size={20} />
                  Get Diet Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-zinc-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">GYM <span className="text-yellow-500">GALLERY</span></h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6"></div>
          <p className="text-center text-gray-400 mb-12 md:mb-16 text-sm md:text-base">Experience world-class facilities and premium equipment</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {[
              'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=600&fit=crop',
              'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=600&h=600&fit=crop',
              'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=600&h=600&fit=crop',
              'https://images.unsplash.com/photo-1593079831268-3381b0db4a77?w=600&h=600&fit=crop',
              'https://images.unsplash.com/photo-1576678927484-cc907957088c?w=600&h=600&fit=crop',
              'https://images.unsplash.com/photo-1558611848-73f7eb4001a1?w=600&h=600&fit=crop'
            ].map((img, idx) => (
              <div key={idx} className="relative group overflow-hidden rounded-lg aspect-square">
                <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trainers */}
      <section id="trainers" className="py-16 md:py-20 px-4 md:px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">MEET OUR <span className="text-yellow-500">TRAINERS</span></h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6"></div>
          <p className="text-center text-gray-400 mb-12 md:mb-16 text-sm md:text-base">Train with certified professionals who are passionate about your success</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {trainers.map((trainer, idx) => (
              <div key={idx} className="bg-zinc-900 rounded-lg overflow-hidden border border-yellow-600 border-opacity-20 hover:border-yellow-500 transition-all duration-300 hover:scale-105">
                <div className="relative h-80 md:h-96 overflow-hidden">
                  <img src={trainer.image} alt={trainer.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                </div>
                <div className="p-4 md:p-6 text-center bg-black">
                  <h3 className="text-lg md:text-xl font-bold mb-1">{trainer.name}</h3>
                  <p className="text-yellow-500 text-xs md:text-sm mb-2">{trainer.title}</p>
                  <p className="text-gray-400 text-xs md:text-sm">{trainer.exp} Experience</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Trial Form */}
      <section id="trial" className="py-16 md:py-20 px-4 md:px-6 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">BOOK YOUR <span className="text-yellow-500">FREE TRIAL</span></h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6"></div>
          <p className="text-center text-gray-400 mb-10 md:mb-12 text-sm md:text-base">Experience the MYFIT difference. No commitment required.</p>
          {successMessage && <div className="bg-green-600 text-white p-3 rounded-lg mb-6 text-center text-sm">{successMessage}</div>}
          <div className="bg-zinc-900 p-6 md:p-8 rounded-lg border border-yellow-600 border-opacity-20">
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2 uppercase tracking-wide">Full Name</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full bg-black border border-yellow-600 border-opacity-30 rounded px-4 py-3 focus:outline-none focus:border-yellow-500 transition" placeholder="Enter your name" />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2 uppercase tracking-wide">Phone Number</label>
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full bg-black border border-yellow-600 border-opacity-30 rounded px-4 py-3 focus:outline-none focus:border-yellow-500 transition" placeholder="+91 XXXXX XXXXX" />
            </div>
            <div className="mb-8">
              <label className="block text-sm font-semibold mb-2 uppercase tracking-wide">Fitness Goal</label>
              <select value={formData.goal} onChange={(e) => setFormData({...formData, goal: e.target.value})} className="w-full bg-black border border-yellow-600 border-opacity-30 rounded px-4 py-3 focus:outline-none focus:border-yellow-500 transition">
                <option value="">Select your goal</option>
                <option value="Weight Loss">Weight Loss</option>
                <option value="Muscle Gain">Muscle Gain</option>
                <option value="General Fitness">General Fitness</option>
                <option value="Strength Training">Strength Training</option>
                <option value="Athletic Performance">Athletic Performance</option>
              </select>
            </div>
            <button onClick={handleSubmit} className="w-full bg-yellow-500 text-black py-3 md:py-4 rounded-full text-base md:text-lg font-bold hover:bg-yellow-400 transition-all duration-300 shadow-lg shadow-yellow-500/50 hover:scale-105">
              Book Free Trial
            </button>
            <p className="text-center text-gray-500 text-xs mt-4">We'll contact you via WhatsApp to schedule your trial session</p>
          </div>
        </div>
      </section>

      {/* Location */}
      <section id="contact" className="py-16 md:py-20 px-4 md:px-6 bg-zinc-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">LOCATION & <span className="text-yellow-500">TIMINGS</span></h2>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-12 md:mb-16"></div>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <div className="space-y-6 md:space-y-8">
              <div className="flex items-start gap-4 bg-black p-6 rounded-lg border border-yellow-600 border-opacity-20">
                <MapPin className="text-yellow-500 flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold mb-2">Address</h3>
                  <p className="text-gray-400 text-sm md:text-base">MYFIT Premium Gym<br />123 Linking Road, Bandra West<br />Mumbai, Maharashtra 400050<br />India</p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-black p-6 rounded-lg border border-yellow-600 border-opacity-20">
                <Clock className="text-yellow-500 flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold mb-2">Operating Hours</h3>
                  <p className="text-gray-400 text-sm md:text-base">Monday - Saturday: 5:00 AM - 11:00 PM<br />Sunday: 6:00 AM - 9:00 PM<br /><span className="text-yellow-500 text-xs">Elite Members: 24/7 Access</span></p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-black p-6 rounded-lg border border-yellow-600 border-opacity-20">
                <Phone className="text-yellow-500 flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold mb-2">Contact Us</h3>
                  <p className="text-gray-400 text-sm md:text-base">Phone: <a href="tel:+919673129769" className="text-yellow-500 hover:text-yellow-400">+91 96731 29769</a><br />Email: <a href="mailto:info@myfit.in" className="text-yellow-500 hover:text-yellow-400">info@myfit.in</a><br />WhatsApp: <button onClick={handleJoinNow} className="text-yellow-500 hover:text-yellow-400">Chat with us</button></p>
                </div>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden border border-yellow-600 border-opacity-20 h-96 md:h-auto">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3770.8937447845694!2d72.82637631490223!3d19.058073687091736!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c929bed1fc97%3A0x6fcc6eb4ab2e3db4!2sBandra%20West%2C%20Mumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin" width="100%" height="100%" style={{ border: 0, minHeight: '400px' }} allowFullScreen="" loading="lazy" title="MYFIT Location"></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-10 md:py-12 px-4 md:px-6 border-t border-yellow-600 border-opacity-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Dumbbell className="text-yellow-500" size={32} />
              <span className="text-2xl md:text-3xl font-bold tracking-wider">MYFIT</span>
            </div>
            <p className="text-gray-400 mb-6 text-base md:text-lg italic max-w-md">"Transform Your Body. Elevate Your Life."</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-10 text-center md:text-left">
            <div>
              <h4 className="text-yellow-500 font-bold mb-3 uppercase text-sm tracking-wide">Quick Links</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#about" className="hover:text-yellow-500 transition">About Us</a></li>
                <li><a href="#membership" className="hover:text-yellow-500 transition">Membership Plans</a></li>
                <li><a href="#nutrition" className="hover:text-yellow-500 transition">Nutrition</a></li>
                <li><a href="#trainers" className="hover:text-yellow-500 transition">Our Trainers</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-yellow-500 font-bold mb-3 uppercase text-sm tracking-wide">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Personal Training</li>
                <li>Group Classes</li>
                <li>Nutrition Consultation</li>
                <li>Swimming Pool Access</li>
                <li>Sports Turf Access</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-yellow-500 font-bold mb-3 uppercase text-sm tracking-wide">Connect With Us</h4>
              <div className="flex justify-center md:justify-start gap-4 mb-4">
                <a href="tel:+919673129769" className="bg-zinc-900 p-3 rounded-full text-gray-400 hover:text-yellow-500 hover:bg-zinc-800 transition">
                  <Phone size={20} />
                </a>
                <a href="mailto:info@myfit.in" className="bg-zinc-900 p-3 rounded-full text-gray-400 hover:text-yellow-500 hover:bg-zinc-800 transition">
                  <Mail size={20} />
                </a>
                <button onClick={handleJoinNow} className="bg-zinc-900 p-3 rounded-full text-gray-400 hover:text-yellow-500 hover:bg-zinc-800 transition">
                  <MessageCircle size={20} />
                </button>
              </div>
            </div>
          </div>
          
          <div className="border-t border-yellow-600 border-opacity-10 pt-8 text-center">
            <p className="text-gray-500 text-xs md:text-sm">© 2024 MYFIT Premium Gym. All rights reserved. | Designed for Champions.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a href="https://wa.me/919673129769" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-2xl hover:bg-green-600 transition-all duration-300 z-50 hover:scale-110" aria-label="Contact us on WhatsApp">
        <MessageCircle size={28} />
      </a>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-24 right-6 bg-yellow-500 text-black p-3 rounded-full shadow-xl hover:bg-yellow-400 transition-all duration-300 z-50 hover:scale-110" aria-label="Scroll to top">
          <ChevronUp size={24} />
        </button>
      )}
    </div>
  );
}